import {
  getAggregatorNames,
  getDefaultDimensionAggregation,
} from '@rawgraphs/rawgraphs-core'

const VALID_AGGREGATORS = new Set(getAggregatorNames())
const FALLBACK_AGGREGATOR = 'sum'

function hasSingleValueArrayMapping(value) {
  return Array.isArray(value) && value.length === 0
}

// Most charts treat an empty optional mapping as absent. The upstream
// multiset-bar mapper is an exception: although `series` is optional in its
// public definition, it unconditionally reads `mapping.series.value`.
const EMPTY_MAPPING_COMPATIBILITY = {
  'rawgraphs.barchartmultiset': new Set(['series']),
}

function preservesEmptyMapping(chart, dimensionId) {
  return Boolean(
    EMPTY_MAPPING_COMPATIBILITY[chart?.metadata?.id]?.has(dimensionId)
  )
}

const isValidName = (name) =>
  typeof name === 'string' && VALID_AGGREGATORS.has(name)

// The aggregator the chart itself would pick for one column, guaranteed to be
// a registered name.
function defaultFor(dimension, dataTypes, column) {
  try {
    const name = getDefaultDimensionAggregation(dimension, dataTypes?.[column])
    return isValidName(name) ? name : FALLBACK_AGGREGATOR
  } catch (e) {
    return FALLBACK_AGGREGATOR
  }
}

/**
 * Return a safe `config.aggregation` for one dimension mapping, or null when it
 * is already fine and can be left by reference.
 *
 * This is the last line of defence before the value reaches the upstream
 * mapper, which looks up whatever it finds here as an aggregator name and
 * throws on anything it does not recognise. Three things have to hold, and each
 * one has been the cause of a crash:
 *
 *  - a scalar has to be widened, because the mapper indexes it and "sum"[0] is
 *    "s";
 *  - every entry has to be a *registered* aggregator, because widening an
 *    already-broken scalar just spreads the damage ("s" becomes ["s","s"]);
 *  - holes have to be filled, because an undefined entry makes the mapper build
 *    an undefined aggregator function and call it.
 *
 * Applied to every dimension, not only the multi-value ones, because the mapper
 * reads config.aggregation for each dimension it gets regardless of whether the
 * dimension declares aggregation at all.
 */
function sanitizeAggregation(currentMapping, dimension, dataTypes) {
  const values = Array.isArray(currentMapping?.value) ? currentMapping.value : []
  const aggregation = currentMapping?.config?.aggregation

  // Absent is the dangerous case, not the safe one. When a dimension
  // aggregates and carries no explicit config, the mapper substitutes
  // getDefaultDimensionAggregation — a bare string — and then indexes it, so
  // "sum"[0] becomes the aggregator name "s". Writing the array out explicitly
  // is what stops that, and it is why this runs even when nothing looks wrong.
  if (aggregation === undefined || aggregation === null) {
    if (!dimension?.aggregation || values.length === 0) return null
    return values.map((column) => defaultFor(dimension, dataTypes, column))
  }

  let asList
  if (typeof aggregation === 'string') {
    asList = Array(Math.max(values.length, 1)).fill(aggregation)
  } else if (Array.isArray(aggregation)) {
    asList = aggregation
  } else {
    return null // a function, which the mapper accepts as-is
  }

  // Replacing an unusable entry: prefer another aggregator the user actually
  // chose on this dimension, and only fall back to the column's own default
  // when there is nothing to borrow.
  const width = Math.max(values.length, asList.length, 1)
  const borrowed = asList.find(isValidName)
  const next = Array.from({ length: width }, (_, index) => {
    if (isValidName(asList[index])) return asList[index]
    return borrowed || defaultFor(dimension, dataTypes, values[index])
  })

  const unchanged =
    Array.isArray(aggregation) &&
    aggregation.length === next.length &&
    next.every((name, index) => name === aggregation[index])

  return unchanged ? null : next
}

export function normalizeChartMapping(chart, mapping = {}, dataTypes) {
  if (!chart?.dimensions || !mapping) {
    return mapping
  }

  let changed = false
  const nextMapping = { ...mapping }

  chart.dimensions.forEach((dimension) => {
    const currentMapping = mapping[dimension.id]

    const safeAggregation = sanitizeAggregation(
      currentMapping,
      dimension,
      dataTypes
    )
    if (safeAggregation) {
      nextMapping[dimension.id] = {
        ...currentMapping,
        config: { ...currentMapping.config, aggregation: safeAggregation },
      }
      changed = true
    }

    if (dimension.multiple) return

    if (preservesEmptyMapping(chart, dimension.id)) {
      if (!currentMapping) {
        nextMapping[dimension.id] = { ids: [], value: [] }
        changed = true
      }
      return
    }

    if (!currentMapping) return

    const ids = Array.isArray(currentMapping.ids) ? currentMapping.ids : []
    if (ids.length > 0) return

    if (hasSingleValueArrayMapping(currentMapping.value)) {
      delete nextMapping[dimension.id]
      changed = true
    }
  })

  return changed ? nextMapping : mapping
}
